import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  PaperClipOutlined,
  UploadOutlined,
  EditOutlined,
  CheckCircleOutlined,
  CloudUploadOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import {
  Form,
  Input,
  InputNumber,
  Table,
  Collapse as AntCollapse,
  Button as AntButton,
  Descriptions,
  Modal as AntModal,
  Spin,
  Flex,
  Drawer,
  Upload,
  Popover,
  Timeline,
  Select,
  DatePicker,
  message,
  Empty,
} from "antd";
import {
  BlockBetween,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  Icon,
} from "../../components/Component";
import dayjs from "dayjs";
import { Badge, Button, Col, Row, Spinner } from "reactstrap";
import invoiceApis from "../../api/Invoice/invoice";
import { nanoid } from "nanoid";
import { Option } from "antd/es/mentions";
import Swal from "sweetalert2";
import axios from "axios";
import usersApis from "../../api/master/users";
import CropperComponent from "../components/CropperComponent";
import SignatureModal from "../purchase-orders/SignatureModal";
import dispatchApis from "../../api/dispatch/dispatch";
import Dragger from "antd/es/upload/Dragger";
import { useDispatch } from "react-redux";
import { fetchSequenceAccess } from "../../store/slices/misc/sequenceAccess";
import { useSelector } from "react-redux";
import { formatCurrency } from "../../utils/Utils";
import FormTextArea from "../form-components/TextAreaComponent";
import { FormProvider, useForm } from "react-hook-form";

const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}: any) => {
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex + `_${record.lineNo}`}
          style={{ margin: 0 }}
          rules={[
            {
              type: "number",
              min: 0.01,
              max:
                Number(record?.quantityInvoiced) -
                Number(record?.quantityDispatched),
              message: `${title} must be in range 1 to ${
                record?.quantityInvoiced - record?.quantityDispatched
              }`,
            },
          ]}
          initialValue={Number(record[dataIndex])}
        >
          <InputNumber
            defaultValue={Number(record[dataIndex])}
            className="w-100"
            formatter={(value) =>{
              if(isNaN(value)) return "";
              const hasDecimal = value.toString().includes(".");
              return value
                ? new Intl.NumberFormat("en-IN", {
                    style: "decimal",
                    minimumFractionDigits: hasDecimal ? 2 : 0,
                    maximumFractionDigits: hasDecimal ? 2 : 0,
                  }).format(value)
                : `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ","); 
            }
            }
            parser={(value: any) => value!.replace(/\$\s?|(,*)/g, "")}
            onKeyPress={(event) => {
              const regex = /^[0-9\b\.]+$/;
              if (!regex.test(event.key)) {
                event.preventDefault();
              }
            }}
            step={0.01}
          />
        </Form.Item>
      ) : (
        children
      )}
    </td>
  );
};

const columns = [
  {
    title: "#",
    key: "#",
    width: 50,
    fixed: "left",
    render: (text: any, record: any, index: number) => index + 1,
  },
  {
    title: "Material Name",
    dataIndex: "materialName",
    key: "materialName",
    width: 200,
    fixed: "left",
    render: (material: any) => <div className="align-left">{material}</div>,
  },
  {
    title: "Need By Date",
    dataIndex: "needByDate",
    key: "needByDate",
    width: 150,
    render: (text: any) => dayjs(text).format("DD-MMM-YYYY"),
  },
  {
    title: "Invoiced Qty",
    dataIndex: "quantityInvoiced",
    key: "quantityInvoiced",
    width: 150,
    render: (text: any, record: any) => (
      <div className="align-left">{Number(text)?.toFixed(2) + " " + record?.uom}</div>
    ),
  },
  {
    title: "Available Qty to Dispatch",
    key: "availableQtyToInvoice",
    width: 200,
    render: (text: any, record: any) => (
      <div className="align-left">
        {Number(Number(record?.quantityInvoiced) -
          Number(record?.quantityDispatched))?.toFixed(2) +
          " " +
          record?.uom}
      </div>
    ),
  },
  {
    title: "Dispatch Qty",
    dataIndex: "dispatchQuantity",
    key: "dispatchQuantity",
    width: 150,
    editable: true,
    render: (text: any, record: any) =>
      text ? (
        <div className="align-left">{Number(text) + " " + record?.uom}</div>
      ) : (
        <></>
      ),
  },
  {
    title: "Unit Price",
    dataIndex: "unitPrice",
    key: "unitPrice",
    width: 150,
    render: (text: any, record: any) => (
      <p className="justify-end">{Number(text)}</p>
    ),
  },
  {
    title: "Basic Amount",
    dataIndex: "basicAmount",
    key: "basicAmount",
    width: 150,
    render: (text: any) => <div>{text}</div>,
  },
  {
    title: "CGST",
    children: [
      {
        title: "%",
        dataIndex: "cgstPercentage",
        key: "cgstPercentage",
        width: 100,
        render: (text: any, record: any) =>
          `${
            Number(record?.sgst) && Number(record?.cgst)
              ? Number(text) + "%"
              : "N/A"
          }`,
      },
      {
        title: "Amount",
        dataIndex: "cgst",
        key: "cgst",
        width: 150,
        render: (text: any, record: any) => (
          <div style={{ textAlign: "right" }}>
            <span>
              {Number(record?.sgst) && Number(record?.cgst)
                ? formatCurrency(text, "INR")
                : "N/A"}
            </span>
          </div>
        ),
      },
    ],
  },
  {
    title: "SGST",
    children: [
      {
        title: "%",
        dataIndex: "sgstPercentage",
        key: "sgstPercentage",
        width: 100,
        render: (text: any, record: any) =>
          `${
            Number(record?.sgst) && Number(record?.cgst)
              ? Number(text) + "%"
              : "N/A"
          }`,
      },
      {
        title: "Amount",
        dataIndex: "sgst",
        key: "sgst",
        width: 150,
        render: (text: any, record: any) => (
          <div style={{ textAlign: "right" }}>
            <span>
              {Number(record?.sgst) && Number(record?.cgst)
                ? formatCurrency(text, "INR")
                : "N/A"}
            </span>
          </div>
        ),
      },
    ],
  },
  {
    title: "IGST",
    children: [
      {
        title: "%",
        dataIndex: "igstPercentage",
        key: "igstPercentage",
        width: 100,
        render: (text: any, record: any) =>
          `${
            !Number(record?.sgst) && !Number(record?.cgst)
              ? Number(text) + "%"
              : "N/A"
          }`,
      },
      {
        title: "Amount",
        dataIndex: "igst",
        key: "igst",
        width: 150,
        render: (text: any, record: any) => (
          <div style={{ textAlign: "right" }}>
            <span>
              {!Number(record?.sgst) && !Number(record?.cgst)
                ? formatCurrency(text, "INR")
                : "N/A"}
            </span>
          </div>
        ),
      },
    ],
  },
  {
    title: "Line Total",
    dataIndex: "grossTotal",
    key: "grossTotal",
    width: 150,
    render: (text: any) => <div>{text}</div>,
  },
  {
    title: "Dispatchs",
    dataIndex: "operation",
    width: 100,
    fixed: "right",
  },
];
const { Panel } = AntCollapse;
function InvoiceComponent({ invoiceNumber, previousPath }: any) {
  const methods = useForm({
    mode: "onChange",
  });
  const {
    watch,
    formState: { errors },
  } = methods;
  const [disableSubmit, setDisableSubmit] = useState(false);
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const [showSubmitBtn, setShowSubmitBtn] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [tab, setTab] = useState("1");
  const [OriginalData, setOriginalData] = useState<any>({});
  const [updatedLineItems, setUpdatedLineItems] = useState<any>([]);
  const [screenSize, setScreenSize] = useState(0);
  const [mobileView, setMobileView] = useState(false);
  const [sm, setSm] = useState(false);
  const [materialData, setMaterialData] = useState<any>({});
  const [signature, setSignature] = useState<any>([]);
  const [DCAttachment, setDCAttachment] = useState<any>(null);
  const [newDCAttachment, setNewDCAttachment] = useState<any>(null);
  const [newInvoiceAttachment, setNewInvoiceAttachment] = useState<any>(null);
  const [InvoiceAttachment, setInvoiceAttachment] = useState<any>(null);
  const [termsVisible, setTermsVisible] = useState(false);
  const [bankDetailsVisible, setBankDetailsVisible] = useState(false);
  const [termsAndConditions, setTermsAndConditions] = useState<any>("");
  const [newTermsAndConditions, setNewTermsAndConditions] = useState<any>("");
  const [dms, setDms] = useState<any>([]);
  const [isTermsAndConditionsChanged, setIsTermsAndConditionsChanged] =
    useState(false);
  const [dataSource, setDataSource] = useState<any>([]);
  const [currency, setCurrency] = useState<any>({
    currencyFormat: "",
    currencySymbol: "",
  });
  const [newInvoiceType, setNewInvoiceType] = useState<any>("");
  const [newInvoiceDate, setNewInvoiceDate] = useState<any>();
  const [newDueDate, setNewDueDate] = useState<any>();
  const [form] = Form.useForm();
  const [form2] = Form.useForm();
  const [additionalCharges, setAdditionalCharges] = useState<any>([]);
  const [editingKeys, setEditingKeys] = useState<string[]>([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [invoiceDisapproveInvoice, setInvoiceDisapproveInvoice] =
    useState<any>(false);
  const [readOnly, setReadOnly] = useState<any>(true);
  const [currentUserId, setCurrentUserId] = useState<any>(null);
  const [approverList, setApproverList] = useState<any>([]);
  const [dispatchableLines, setDispatchableLines] = useState<any>([]);
  const [showCropModel, setShowCropModel] = useState<boolean>(false);
  const [dispatchSignature, setDispatchSignature] = useState<any>({});
  const [showSignatureModel, setShowSignatureModel] = useState<boolean>(false);
  const { data: sequenceAccess } = useSelector(
    (state: any) => state.sequenceAccess
  );

  const termsAndConditionsWatch = watch("termsAndConditions");

  useEffect(() => {
    if (termsAndConditionsWatch) {
      setNewTermsAndConditions(termsAndConditionsWatch);
    }
  }, [termsAndConditionsWatch]);

  // --------------------------------------- functions ---------------------------------------
  useEffect(() => {
    dispatch(fetchSequenceAccess());
  }, [dispatch]);

  const isEditing = (record: any) => editingKeys.includes(record.lineNo);

  const mergedColumns: any = columns.map((col: any) => {
    if (col.dataIndex == "operation") {
      return {
        title: "Dispatchs",
        dataIndex: "operation",
        width: 100,
        fixed: "right",
        render: (text: any, record: any) => {
          return !["Pending"].includes(record?.dispatchStatus) ? (
            <Button
              size="sm"
              color=""
              onClick={() => {
                navigate(
                  `/view-material-dispatch/${invoiceNumber}/${record.lineNo}`
                );
              }}
            >
              <Icon name="focus" />
              {/* {JSON.stringify(record)} */}
            </Button>
          ) : (
            <></>
          );
        },
      };
    }
    if (
      [
        "basicAmount",
        "netAmount",
        "additionalCharges",
        "grossTotal",
        "unitPrice",
      ].includes(col?.dataIndex as string)
    ) {
      return {
        ...col,
        render: (text: any) => (
          <p className="justify-end">
            {formatCurrency(Number(text), currency?.currencyCode)}
          </p>
        ),
      };
    } else if ((col.children?.length as number) > 0) {
      return {
        ...col,
        // children: col?.children?.map((child: any) => {
        //   if (child?.title == "Amount") {
        //     return {
        //       ...child,
        //       render: (text: any) => (
        //         <p className="justify-end">
        //           {formatCurrency(
        //             Number(text),
        //             currency?.currencyCode
        //           )}
        //         </p>
        //       ),
        //     };
        //   } else {
        //     return child;
        //   }
        // }),
      };
    }
    if (
      materialData?.needByDateType != "Line Level" &&
      col?.dataIndex == "needByDate"
    ) {
      return {
        ...col,
        hidden: true,
      };
    }
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: any) => ({
        record,
        inputType: col.dataIndex === "quantity" ? "number" : "text",
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    };
  });

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
    if (!newSelectedRowKeys) {
      editingKeys.forEach((key) => {
        const row = dataSource.find((item: any) => item.lineNo == key);
        form.setFieldsValue({ [`dispatchQuantity_${row.lineNo}`]: null });
      });
      setEditingKeys(() => []);
      form.resetFields();
    }
    const unSelectedRowKeys = selectedRowKeys.filter(
      (key) => !newSelectedRowKeys.includes(key)
    );
    unSelectedRowKeys?.forEach((key) => {
      const row = dataSource.find((item: any) => item.lineNo == key);
      form.setFieldsValue({ [`dispatchQuantity_${row.lineNo}`]: null });
    });
    const editableRowKeys: any = [];
    newSelectedRowKeys?.forEach((key) => {
      const row = dataSource.find((item: any) => item.lineNo == key);
      if (row) {
        form.setFieldsValue({ ...row });
        editableRowKeys.push(row.lineNo);
      }
    });
    setEditingKeys(editableRowKeys);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const showDrawer = async () => {
    try {
      const rows = await form.validateFields();
      const dispatchLines: any = [];
      dataSource.forEach((item: any) => {
        if (
          editingKeys.includes(item.lineNo) &&
          Number(rows[`dispatchQuantity_` + item.lineNo]) > 0
        ) {
          dispatchLines.push({
            ...item,
            dispatchQuantity: rows[`dispatchQuantity_` + item.lineNo],
          });
        }
      });
      setDispatchableLines(dispatchLines);
      setDrawerVisible(true);
    } catch (error) {
      console.log(error);
    }
  };

  const closeDrawer = () => {
    setDrawerVisible(false);
    form2.resetFields();
  };

  const fetchCurrentUser = async () => {
    try {
      const data = await usersApis.getCurrentUser();
      if (data) {
        setCurrentUserId(data?.id);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const viewChange = () => {
    setScreenSize(window.innerWidth);
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  const fetchHtmlContentFromUrl = async (url: string) => {
    try {
      const response = await axios.get(url, {
        headers: {
          "Content-Type": "text/html",
        },
      });

      return response?.data;
    } catch (error) {
      console.log(error);
      return null;
    }
  };

  const fetchInvoiceData = async () => {
    try {
      const response = await invoiceApis.fetchInvoice(invoiceNumber);
      const { status, message, data } = response;
      if (status) {
        if (data?.orderApprovals) {
          let ids = data?.orderApprovals?.map((item: any) => item?.userId);
          ids = ids?.filter((item: any) => status != "Approved");
          setApproverList(ids);
        }
        if (data?.additionalChargesArray) {
          setAdditionalCharges(
            data?.additionalChargesArray?.map((charge: any) => {
              const id = nanoid(4);
              form2.setFieldsValue({
                [`AdditionalCharges.${id}.name`]: charge?.name,
                [`AdditionalCharges.${id}.price`]: Number(charge?.price),
              });
              return {
                ...charge,
                id,
              };
            })
          );
        }
        if (data?.invoiceLines) {
          setDataSource(data?.invoiceLines);
        }
        if (data?.dms?.length > 0) {
          const dms = data?.dms;
          setDms(dms);
          const _signature = dms?.find(
            (item: any) => item.moduleFileType == "signature"
          );
          if (_signature) {
            setSignature({
              docId: _signature?.docId,
              fileName: _signature?.fileName,
              image: _signature?.filePath,
              moduleFileType: _signature?.moduleFileType,
              module: _signature?.module,
              label: data?.signatureLabel,
              isActive: true,
            });
          }
          const _termsAndConditions = dms?.find(
            (item: any) =>
              item.moduleFileType == "termsAndConditions" &&
              item.module == "invoice"
          );
          if (_termsAndConditions) {
            const data: any = await fetchHtmlContentFromUrl(
              _termsAndConditions?.filePath
            );
            setTermsAndConditions(data);
            setNewTermsAndConditions(data);
          }

          const _DCAttachment = dms?.find(
            (item: any) =>
              item.moduleFileType == "DCAttachment" && item.module == "invoice"
          );
          if (_DCAttachment) {
            setDCAttachment(_DCAttachment);
          }

          const _InvoiceAttachment = dms?.find(
            (item: any) =>
              item.moduleFileType == "InvoiceAttachment" &&
              item.module == "invoice"
          );
          if (_InvoiceAttachment) {
            setInvoiceAttachment(_InvoiceAttachment);
          }
        }
        setCurrency({
          currencyCode: data?.currencyCode,
          currency: data?.currencyName,
          currencySymbol: data?.currencySymbol,
          currencyFormat: data?.currencyFormat,
        });
        // if (
        //   [
        //     "Pending Approval",
        //     "Approval Rejected",
        //     "To be Dispatched",
        //     "Partially Dispatched",
        //   ].includes(data.invoiceStatus) &&
        //   data?.invoiceType != "commercial"
        // ) {
        //   setReadOnly(false);
        // } else {
        //   setReadOnly(true);
        // }
        setMaterialData(data);
        setOriginalData(data);
        setShowSubmitBtn(false);
        setIsLoading(false);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const createDispatch = async (Data: any) => {
    try {
      const _formData = new FormData();
      const filesData = [];
      const dispatchData: any = {
        invoiceNumber: Number(invoiceNumber),
        dispatchId: Data?.DispatchId,
        mode: "F",
        dispatchDate: dayjs(Data?.dispatchDate).format("YYYY-MM-DD"),
        driverInfo: {
          name: Data?.driverName,
          mobile: Data?.driverContact,
          email: Data?.driverEmail,
          vehicleNumber: Data?.vehicleNumber,
          vehicleType: Data?.vehicleType,
        },
        deliveryAddressId: OriginalData?.deliveryAddressId,
        autoRelease: false,
        lineItems: dispatchableLines?.map((item: any) => {
          return {
            invoiceLineNo: Number(item?.lineNo),
            quantityDispatched: Number(item?.dispatchQuantity),
          };
        }),
      };
      if (dispatchSignature?.image) {
        const image = base64ToFile(dispatchSignature?.image, "signature.png");
        dispatchData.signatureLabel = dispatchSignature?.label;
        _formData.append("files", image);
        filesData.push({
          indexOfFileLocated: filesData.length,
          purpose: "signature",
          module: "dispatch",
          level: "Header Level",
          uniqueId: nanoid(4),
          action: "Create",
        });
      }
      if (attachments?.length > 0) {
        attachments?.forEach((item: any) => {
          _formData.append("files", item.originFileObj);
          filesData.push({
            indexOfFileLocated: filesData.length,
            purpose: "attachment",
            module: "dispatch",
            level: "Header Level",
            uniqueId: nanoid(4),
            action: "Create",
          });
        });
      }
      _formData.append("dispatchData", JSON.stringify(dispatchData));
      _formData.append("filesData", JSON.stringify(filesData));

      const response = await dispatchApis.submitDispatch(_formData);
      const { status, message, data: _data } = response;

      if (status) {
        Swal.fire("Dispatch Created", _data, "success");
        navigate("/view-dispatch-list");
      } else {
        Swal.fire("Error", message, "error");
      }
    } catch (error) {
      console.log(error);
    }
    setDisableSubmit(false);
  };
  function base64ToFile(base64String: string, fileName: string) {
    const byteString = atob(base64String.split(",")[1]);
    const mimeString = base64String.split(",")[0].split(":")[1].split(";")[0];
    const arrayBuffer = new ArrayBuffer(byteString.length);
    const uint8Array = new Uint8Array(arrayBuffer);

    for (let i = 0; i < byteString.length; i++) {
      uint8Array[i] = byteString.charCodeAt(i);
    }

    const blob = new Blob([arrayBuffer], { type: mimeString });
    return new File([blob], fileName, { type: mimeString });
  }

  const SubmitChanges = async () => {
    try {
      const _formData = new FormData();

      const newfiledata: any = [];
      const deletedFileData: any = [];
      const updatedFileData: any = [];
      const _updatedLineItems: any = [];
      dataSource?.forEach((item: any) => {
        if (updatedLineItems?.includes(item?.lineNo)) {
          _updatedLineItems.push({
            lineNo: Number(item?.lineNo),
            materialId: Number(item?.materialId),
            quantityInvoiced: Number(item?.quantityInvoiced),
          });
        }
      }, []);

      if (isTermsAndConditionsChanged) {
        const termsAndConditionsFile = new Blob([newTermsAndConditions], {
          type: "text/html",
        });
        const _termsAndConditions = dms?.find(
          (item: any) => item.moduleFileType == "termsAndConditions"
        );
        if (_termsAndConditions?.docId) {
          deletedFileData.push({
            docId: _termsAndConditions.docId,
          });
        }
        _formData.append(
          `files`,
          new File([termsAndConditionsFile], "termsAndConditions.html", {
            type: "text/html",
          })
        );
        newfiledata.push({
          indexOfFileLocated: newfiledata.length,
          module: "invoice",
          purpose: "termsAndConditions",
          level: "Header Level",
          uniqueId: nanoid(6),
          action: "Create",
        });
        setIsTermsAndConditionsChanged(false);
        setNewTermsAndConditions(null);
      }
      if (newDCAttachment) {
        newfiledata.push({
          indexOfFileLocated: newfiledata.length,
          module: "invoice",
          purpose: "DCAttachment",
          level: "Header Level",
          uniqueId: nanoid(6),
          action: "Create",
        });
        if (DCAttachment)
          deletedFileData.push({
            docId: DCAttachment?.docId,
          });
        _formData.append(
          `files`,
          new File([newDCAttachment], newDCAttachment?.name, {
            type: newDCAttachment?.type,
          })
        );
        setNewDCAttachment(null);
      }
      if (newInvoiceAttachment) {
        newfiledata.push({
          indexOfFileLocated: newfiledata.length,
          module: "invoice",
          purpose: "InvoiceAttachment",
          level: "Header Level",
          uniqueId: nanoid(6),
          action: "Create",
        });
        if (InvoiceAttachment)
          deletedFileData.push({
            docId: InvoiceAttachment?.docId,
          });
        _formData.append(
          `files`,
          new File([newInvoiceAttachment], newInvoiceAttachment?.name, {
            type: newInvoiceAttachment?.type,
          })
        );
        setNewInvoiceAttachment(null);
      }

      const filesData = {
        newfiledata,
        deletedFileData,
        updatedFileData,
      };
      const invoiceData: any = {
        mode: "U",
        combinedLineItems: {
          existingLineItems: OriginalData?.invoiceLines?.map((item: any) => {
            return {
              lineNo: item?.lineNo,
              materialId: item?.materialId,
              quantityInvoiced: Number(item?.quantityInvoiced),
            };
          }),
          updatedLineItems: _updatedLineItems,
          newLineItems: [],
          deletedLineItems: [],
        },
        additionalChargesArray: additionalCharges?.map((charge: any) => {
          return {
            name: charge?.name,
            price: charge?.price,
          };
        }),
      };
      if (newInvoiceType != "") {
        invoiceData.invoiceType = newInvoiceType;
        setNewInvoiceType("");
      }
      if (newInvoiceDate) {
        invoiceData.invoiceDate = newInvoiceDate?.format("YYYY-MM-DD");
        setNewInvoiceDate(null);
      }
      if (newDueDate) {
        invoiceData.dueDate = newDueDate?.format("YYYY-MM-DD");
        setNewDueDate(null);
      }
      _formData.append("invoiceData", JSON.stringify(invoiceData));
      _formData.append("filesData", JSON.stringify(filesData));
      _formData.append("files", JSON.stringify([]));
      const response = await invoiceApis.amendmentInvoice(
        invoiceNumber,
        _formData
      );
      const { status, data, message } = response;
      if (status) {
        Swal.fire({
          icon: "success",
          title: "Successfully Updated",
          showConfirmButton: false,
          timer: 1000,
        });

        fetchInvoiceData();
      } else {
        Swal.fire({
          icon: "error",
          title: message,
          showConfirmButton: false,
          timer: 1000,
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Something went wrong",
        showConfirmButton: false,
        timer: 1000,
      });
      console.log(error);
    }
  };

  const [attachments, setAttachments] = useState([]);

  const beforeUpload = (file: any) => {
    const isLt2M = file.size / 1024 / 1024 <= 2;
    const isValidType = ["application/pdf", "image/jpeg", "image/png"].includes(
      file.type
    );

    if (!isLt2M) {
      Swal.fire({
        icon: "error",
        title: "File size should be less than 2MB",
      });
    }

    if (!isValidType) {
      Swal.fire({
        icon: "error",
        title: "File type should be pdf, jpeg or png",
      });
    }

    return isLt2M && isValidType;
  };

  const handleChange = (info: any) => {
    let files = info.fileList.slice(-3); // Limit to 3 files
    setAttachments(files);
  };

  useEffect(() => {
    fetchCurrentUser();
    fetchInvoiceData();
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);

  useEffect(() => {
    if (invoiceNumber) {
      setIsLoading(true);
      fetchInvoiceData();
    }
  }, [invoiceNumber]);

  const ShowBillingAddress = ({ address }: any) => {
    if (!address) return <></>;
    const {
      deliveryLocationName,
      addressLine1,
      addressLine2,
      city,
      state,
      countryInfo,
      pincode,
      district,
    } = address;
    const { cin, gstin, pan, phoneNumber } = OriginalData?.orgDetails;
    return (
      <ul>
        <li>
          <strong style={{ color: "#2263b3" }}>{deliveryLocationName}</strong>
        </li>
        <li>{addressLine1}</li>
        <li>{addressLine2}</li>
        <li>
          {city},{district}, {state}, {countryInfo} - {pincode}
        </li>
        <li>
          <strong>GSTIN</strong> : {gstin}
        </li>
        <li>
          <strong>CIN</strong> : {cin}
        </li>
        <li>
          <strong>PAN</strong> : {pan}
        </li>
        <li>
          <strong>Phone</strong> : {phoneNumber}
        </li>
      </ul>
    );
  };
  const ShowDeliveryAddress = ({ address }: any) => {
    if (!address) return <></>;
    const {
      deliveryLocationName,
      addressLine1,
      addressLine2,
      city,
      state,
      countryInfo,
      pincode,
      district,
    } = address;

    return (
      <ul>
        <li>
          <strong style={{ color: "#2263b3" }}>
            {address?.deliveryLocationName}
          </strong>
        </li>
        <li>
          {addressLine1},{addressLine2}
        </li>
        <li>
          {city},{district}, {state}, {countryInfo} - {pincode}
        </li>
        <li>
          <strong>Contact Person</strong> : {address?.contactPersonName}
        </li>
        <li>
          <strong>Mobile Number</strong> : {address?.mobileNumber}
        </li>
      </ul>
    );
  };

  const ShowSupplierBankInfo = () => {
    if (!OriginalData?.supplierDetails) return <></>;

    const { cin, gstin, pan, phoneNumber, bankDetails, address } =
      OriginalData?.supplierDetails || {};

    const { bankName, accountNumber, accountHolderName, branch, ifscCode } =
      bankDetails || {};

    const { city, stateInfo, countryInfo, pincode } = address || {};

    return (
      <Descriptions
        title="Supplier Bank Details "
        bordered
        column={1}
        size="small"
      >
        <Descriptions.Item label="GSTIN">{gstin}</Descriptions.Item>
        <Descriptions.Item label="CIN">{cin}</Descriptions.Item>
        <Descriptions.Item label="PAN">{pan}</Descriptions.Item>
        <Descriptions.Item label="Phone">{phoneNumber}</Descriptions.Item>
        <Descriptions.Item label="Bank Name">{bankName}</Descriptions.Item>
        <Descriptions.Item label="Account Number">
          {accountNumber}
        </Descriptions.Item>
        <Descriptions.Item label="Account Holder Name">
          {accountHolderName}
        </Descriptions.Item>
        <Descriptions.Item label="Branch">{branch}</Descriptions.Item>
        <Descriptions.Item label="IFSC Code">{ifscCode}</Descriptions.Item>
        <Descriptions.Item label="City">{city}</Descriptions.Item>
        <Descriptions.Item label="State">
          {stateInfo?.stateName}
        </Descriptions.Item>
        <Descriptions.Item label="Country">
          {countryInfo?.countryName}
        </Descriptions.Item>
        <Descriptions.Item label="Pincode">{pincode}</Descriptions.Item>
      </Descriptions>
    );
  };

  const ApprovalTimelineContent = () => {
    const approvalsData = materialData?.invoiceApprovals;

    if (!approvalsData || approvalsData?.length === 0) {
      return <Badge color="outline-danger">No</Badge>;
    }

    return (
      <Popover
        content={
          <Timeline className="approval-timeline">
            {approvalsData?.map((approval: any, index: number) => {
              const isApproved = approval.status == "Approved";
              const isRejected = approval.status == "Rejected";
              return (
                <Timeline.Item
                  key={index}
                  dot={
                    isApproved ? (
                      <CheckCircleOutlined style={{ color: "green" }} />
                    ) : isRejected ? (
                      <CloseCircleOutlined style={{ color: "red" }} />
                    ) : undefined
                  }
                >
                  {approval?.user?.firstName}
                  <div className="designation">{approval?.role?.roleName}</div>
                  {approval?.approvalDate && (
                    <div className="designation">
                      {dayjs(approval?.approvalDate).format("DD-MMM-YYYY")}
                    </div>
                  )}
                </Timeline.Item>
              );
            })}
          </Timeline>
        }
        title={<span style={{ color: "#2263b3" }}>Approval Hierarchy</span>}
      >
        <span
          style={{
            cursor: "pointer",
          }}
        >
          <Badge color="outline-success">Yes</Badge>
        </span>
      </Popover>
    );
  };

  const dcFileUploadProps = {
    beforeUpload: (file: any) => {
      // Perform any file validation or processing here
      setNewDCAttachment(file);
      setShowSubmitBtn(true);
      return false; // Return false to prevent auto-upload
    },
    maxCount: 1,
    onRemove: () => {
      setNewDCAttachment(null); // Clear the file from state
    },
  };
  const invoiceFileUploadProps = {
    beforeUpload: (file: any) => {
      // Perform any file validation or processing here
      setShowSubmitBtn(true);
      setNewInvoiceAttachment(file);
      return false; // Return false to prevent auto-upload
    },
    maxCount: 1,
    onRemove: () => {
      setNewInvoiceAttachment(null); // Clear the file from state
    },
  };

  const ApproveInvoice = async () => {
    try {
      const response = await invoiceApis.approveInvoice(invoiceNumber);
      if (response.status) {
        Swal.fire("Approved!", "Invoice has been approved", "success");
      }
    } catch (error) {}
  };

  const DisapproveInvoice = async (reasonForRejection: string) => {
    try {
      const response = await invoiceApis.disapproveInvoice(
        invoiceNumber,
        reasonForRejection
      );
      if (response.status) {
        Swal.fire("Rejected!", "Invoice has been rejected", "success");
      }
    } catch (error) {}
  };

  return (
    <div className="card-inner card-inner-lg">
      {sm && mobileView && (
        <div className="toggle-overlay" onClick={() => setSm(!sm)}></div>
      )}
      <BlockHead size="lg">
        <BlockBetween>
          <BlockHeadContent>
            <BlockTitle tag="h4">
              <span>
                {OriginalData?.orderId} | {OriginalData?.invoiceId}{" "}
              </span>
            </BlockTitle>
          </BlockHeadContent>

          <RejectInvoice
            isModalVisible={invoiceDisapproveInvoice}
            setIsModalVisible={setInvoiceDisapproveInvoice}
            rejectInvoice={DisapproveInvoice}
          />

          <div className=" d-flex">
            {["Pending Approval"].includes(OriginalData?.invoiceStatus) &&
              approverList.includes(currentUserId) &&
              !showSubmitBtn && (
                <div className="actions clearfix">
                  <ul
                    className="m-auto d-flex align-items-center justify-content-center p-0"
                    style={{
                      flexDirection: "row-reverse",
                    }}
                  >
                    <li className="pt-0 pb-0">
                      <Button
                        color="success"
                        type="button"
                        onClick={() => {
                          Swal.fire({
                            title: "Are you sure?",
                            text: "Do you want to approve this invoice?",
                            icon: "warning",
                            showCancelButton: true,
                            confirmButtonText: "Yes, Approve it!",
                            cancelButtonText: "No, Keep it!",
                          }).then((result) => {
                            if (result.isConfirmed) {
                              ApproveInvoice();
                            }
                          });
                        }}
                      >
                        Approve
                      </Button>
                    </li>
                    <li className="pt-0 pb-0">
                      <Button
                        color="danger"
                        onClick={() => {
                          setInvoiceDisapproveInvoice(true);
                        }}
                        type="button"
                        className="btn-dim btn btn-secondary"
                      >
                        Reject
                      </Button>
                    </li>
                  </ul>
                </div>
              )}
            {showSubmitBtn && !readOnly ? (
              <div className="actions clearfix paddingLeft10">
                <ul
                  className="m-auto d-flex align-items-center justify-content-center p-0"
                  style={{
                    flexDirection: "row-reverse",
                  }}
                >
                  <li className="pt-0 pb-0">
                    <Button
                      color="primary"
                      type="button"
                      onClick={() => {
                        Swal.fire({
                          title: "Are you sure?",
                          text: "Do you want to submit the changes?",
                          icon: "warning",
                          showCancelButton: true,
                          confirmButtonColor: "#3085d6",
                          cancelButtonColor: "#d33",
                          confirmButtonText: "Yes, submit it!",
                          cancelButtonText: "No, cancel",
                        }).then(async (result) => {
                          if (result.isConfirmed) {
                            await SubmitChanges();
                          }
                        });
                      }}
                    >
                      Submit
                    </Button>
                  </li>
                  <li className="p-0">
                    <Button
                      color="secondary"
                      onClick={() => {
                        Swal.fire({
                          title: "Are you sure?",
                          text: "Do you want to revert the changes?",
                          icon: "warning",
                          showCancelButton: true,
                          confirmButtonColor: "#3085d6",
                          cancelButtonColor: "#d33",
                          confirmButtonText: "Yes, revert it!",
                          cancelButtonText: "No, cancel",
                        }).then((result) => {
                          if (result.isConfirmed) {
                            fetchInvoiceData();
                            Swal.fire(
                              "Reverted!",
                              "Your changes have been reverted.",
                              "success"
                            );
                          }
                        });
                      }}
                      type="button"
                      className="btn-dim btn btn-secondary"
                    >
                      Revert
                    </Button>
                  </li>
                </ul>
              </div>
            ) : (
              <div className="paddingLeft10">
                <Button
                  className="toggle"
                  color="primary"
                  onClick={async () => {
                    navigate(previousPath);
                  }}
                >
                  <Icon name="arrow-left" />
                  <span>Back</span>
                </Button>
              </div>
            )}
          </div>
        </BlockBetween>
      </BlockHead>
      {isLoading == true ? (
        <>
          <Flex align="center" gap="middle" className="pageLoader">
            <Spin size="large" className="m-auto" />
          </Flex>
        </>
      ) : (
        <div className="content clearfix">
          <AntCollapse defaultActiveKey={["1", "2", "3"]}>
            <Panel header="General Information" key="1">
              <div className="tab-content mt-0">
                <div className={`tab-pane${tab === "1" && " active"}`}>
                  <Col className="justify-start w-100  flex-wrap  p-0">
                    <div className="w-30 p-1 pt-0 pb-0 border-end">
                      <div className="data-col align-center p-0 pt-1">
                        <div className="data-label w-40">Invoice Type</div>
                        <div className="data-value w-50">
                          {materialData?.invoiceType != "commercial" &&
                          !readOnly ? (
                            <Form.Item
                              name="invoiceType"
                              label={undefined}
                              className="w-100 paddingLeft10  p-0 m-0"
                              initialValue={materialData?.invoiceType}
                            >
                              <Select
                                placeholder="Select Invoice Type"
                                defaultValue={materialData?.invoiceType}
                                onChange={(e) => {
                                  setNewInvoiceType(e);
                                  setShowSubmitBtn(true);
                                }}
                              >
                                {materialData?.invoiceType == "dc" ? (
                                  <Option value="dc">Delivery Challan</Option>
                                ) : (
                                  <Option value="proforma">
                                    Proforma Invoice
                                  </Option>
                                )}
                                <Option value="commercial">
                                  Commercial Invoice
                                </Option>
                              </Select>
                            </Form.Item>
                          ) : (
                            <span>
                              {materialData?.invoiceType == "commercial"
                                ? "Commercial Invoice"
                                : materialData?.invoiceType == "dc"
                                ? "Delivery Challan"
                                : "Proforma Invoice"}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="data-col align-center p-0 pt-1">
                        <div className="data-label w-40">Invoice Date</div>
                        <div className="data-value w-50">
                          {!readOnly ? (
                            <Form.Item
                              name="invoiceDate"
                              label={undefined}
                              className="w-100 paddingLeft10  p-0 m-0"
                              initialValue={materialData?.invoiceDate}
                            >
                              <DatePicker
                                className="w-100"
                                placeholder="Invoice Due Date"
                                maxDate={dayjs().add(1, "day")}
                                minDate={dayjs(
                                  OriginalData?.orderData
                                ).subtract(1, "day")}
                                allowClear={false}
                                defaultValue={dayjs(materialData?.invoiceDate)}
                                onChange={(e) => {
                                  setNewInvoiceDate(e);
                                  setShowSubmitBtn(true);
                                }}
                              />
                            </Form.Item>
                          ) : (
                            <span>
                              {dayjs(materialData?.invoiceDate).format(
                                "DD-MMM-YYYY"
                              )}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="data-col align-center p-0 pt-1">
                        <div className="data-label w-40">Due Date</div>
                        <div className="data-value w-50">
                          {!readOnly ? (
                            <Form.Item
                              name="dueDate"
                              label={undefined}
                              className="w-100 paddingLeft10  p-0 m-0"
                              initialValue={materialData?.dueDate}
                            >
                              <DatePicker
                                className="w-100"
                                placeholder="Invoice Due Date"
                                defaultValue={dayjs(materialData?.dueDate)}
                                allowClear={false}
                                onChange={(e) => {
                                  setNewDueDate(e);
                                  setShowSubmitBtn(true);
                                }}
                                minDate={
                                  newInvoiceDate
                                    ? dayjs(newInvoiceDate)?.add(
                                        Number(materialData?.creditDays) || 0,
                                        "day"
                                      )
                                    : dayjs(materialData?.invoiceDate)
                                }
                              />
                            </Form.Item>
                          ) : (
                            <span>
                              {dayjs(materialData?.dueDate).format(
                                "DD-MMM-YYYY"
                              )}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="data-col align-center pt-1">
                        <div className="data-label w-40">Approvals</div>
                        <div className="data-value w-50">
                          <ApprovalTimelineContent />
                        </div>
                      </div>
                    </div>

                    <div className="w-30  paddingLeft10 border-end">
                      <div className=" pt-0">
                        <div className="data-label w-90 mb-1">
                          Billing Address
                        </div>
                        <div className="data-value w-90">
                          <ShowBillingAddress
                            address={materialData?.billingAddress}
                          />
                        </div>
                      </div>
                    </div>
                    {materialData?.deliveryAddressId && (
                      <div className="w-40 paddingLeft10 ">
                        <div className=" pt-0">
                          <div className="data-label w-9 mb-1">
                            Delivery Address
                          </div>
                          <div className="data-value w-90">
                            <ShowDeliveryAddress
                              address={materialData?.deliveryAddress}
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </Col>
                </div>
              </div>
            </Panel>
            <Panel header="Material Information" key="2">
              <Form form={form} component={false}>
                <div className="d-flex justify-content-end m-0 p-0">
                  <Button
                    color="primary"
                    className="m-0"
                    style={{ height: 40 }}
                    onClick={showDrawer}
                    disabled={selectedRowKeys.length === 0}
                  >
                    Preview Dispatch
                  </Button>
                </div>
                <div className="table-responsive poTable">
                  <div
                    style={{
                      width: screenSize - 380 + "px",
                      margin: "0px auto",
                    }}
                  >
                    <Table
                      className="customTable"
                      columns={mergedColumns}
                      rowSelection={rowSelection}
                      components={{
                        body: {
                          cell: EditableCell,
                        },
                      }}
                      dataSource={dataSource.map((item: any) => ({
                        ...item,
                        key: item?.lineNo,
                      }))}
                      scroll={{ x: 1000 }}
                      expandable={{
                        expandedRowRender: (record: any) => (
                          <div className="expandableBody">
                            <div style={{ flex: 1, textAlign: "left" }}>
                              <p style={{ margin: 0 }}>{record.description}</p>
                              {record.file && (
                                <div>
                                  <PaperClipOutlined /> &nbsp;
                                  <a
                                    href={record?.file?.filePath}
                                    download={record?.file?.filePath}
                                    target="_blank"
                                  >
                                    {record?.file?.fileName}
                                  </a>
                                </div>
                              )}
                            </div>
                            {materialData?.deliveryAddressType ===
                              "Line Level" &&
                              record.deliveryAddressId && (
                                <div
                                  className="lineAddress"
                                  style={{ flex: 1 }}
                                >
                                  <h6>Delivery Address</h6>
                                  <ShowDeliveryAddress
                                    address={record.deliveryAddress}
                                  />
                                </div>
                              )}
                          </div>
                        ),
                      }}
                      bordered
                      size="middle"
                      pagination={false}
                    />
                  </div>
                  <div className="d-flex">
                    <Row className="mt-2 w-50"></Row>

                    {/* <ViewTableTotals
                      data={dataSource}
                      additionalCharges={additionalCharges}
                      invoice={true}
                    /> */}
                  </div>
                  <Row className="m-4 justify-content-end ">
                    {signature?.isActive && (
                      <div className="signatureBtnWrapper">
                        <div className="d-flex justify-content-between align-items-center">
                          <strong>Signature</strong>
                        </div>
                        <div
                          style={{
                            minHeight: signature?.image ? "0px" : "120px",
                            display: signature?.image ? "block" : "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {signature?.image && (
                            <>
                              <div className="signatueImgWrapper border">
                                <img
                                  src={signature?.image}
                                  alt="Signed"
                                  style={{ width: "160px" }}
                                />
                              </div>

                              <div>
                                <div className="form-group">
                                  <label
                                    className="form-label"
                                    htmlFor="signatureLabel"
                                  >
                                    Signature Label
                                  </label>
                                  <input
                                    type="text"
                                    className="form-control"
                                    name="signatureLabel"
                                    onChange={(e) => {}}
                                    disabled
                                    defaultValue={
                                      signature?.label || "Authorised Signatory"
                                    }
                                  />
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    )}
                  </Row>
                </div>
              </Form>
            </Panel>
            <Panel header="Other Information" key="3">
              <div className="w-100 d-flex">
                <Descriptions
                  title=""
                  className="custom-description-title w-30"
                >
                  <Descriptions.Item label="Terms and Conditions">
                    <span
                      style={{
                        color: "#2263b3",
                        textDecoration: "underline",
                        cursor: "pointer",
                      }}
                      onClick={() => setTermsVisible(true)}
                    >
                      View
                    </span>
                  </Descriptions.Item>
                </Descriptions>

                <div className="w-30 m-3 mt-0 mb-0">
                  <div>
                    <strong>Invoice Attachment &nbsp; &nbsp;</strong>
                    {!readOnly && (
                      <Upload {...invoiceFileUploadProps}>
                        <CloudUploadOutlined />
                      </Upload>
                    )}
                  </div>
                  {InvoiceAttachment && !newInvoiceAttachment && (
                    <div>
                      <a
                        href={InvoiceAttachment?.filePath}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {InvoiceAttachment?.fileName}
                      </a>
                    </div>
                  )}
                </div>

                <div className="w-30 m-3 mt-0 mb-0">
                  <strong>Attach Delivery Challan &nbsp; &nbsp;</strong>
                  {!readOnly && (
                    <Upload {...dcFileUploadProps} className="w-50">
                      <CloudUploadOutlined />
                    </Upload>
                  )}
                  {DCAttachment && !newDCAttachment && (
                    <div>
                      <a
                        href={DCAttachment?.filePath}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {DCAttachment?.fileName}
                      </a>
                    </div>
                  )}
                </div>

                <div className="w-30 m-3 mt-0 mb-0">
                  <strong>Bank Details &nbsp; &nbsp;</strong>
                  <span
                    style={{
                      color: "#2263b3",
                      textDecoration: "underline",
                      cursor: "pointer",
                    }}
                    onClick={() => setBankDetailsVisible(true)}
                  >
                    View
                  </span>
                </div>
              </div>
            </Panel>
          </AntCollapse>

          <AntModal
            title={undefined}
            visible={termsVisible}
            footer={null}
            onCancel={() => {
              setIsTermsAndConditionsChanged(false);
              setTermsVisible(false);
            }}
            width="60%"
            bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
          >
            <div className="d-flex">
              <h5 className="m-3">Terms and Conditions</h5>
              {!isTermsAndConditionsChanged && !readOnly && (
                <EditOutlined
                  onClick={() => setIsTermsAndConditionsChanged(true)}
                />
              )}
            </div>
            {isTermsAndConditionsChanged ? (
              // <ReactQuill
              //   theme="snow"
              //   value={newTermsAndConditions}
              //   onChange={(data) => {
              //     setNewTermsAndConditions(data);
              //   }}
              //   className="mb-3"
              // />
              <FormProvider {...methods}>
                <form>
                  <div className="mt-1 mb-3">
                    <FormTextArea
                      defaultValue={newTermsAndConditions}
                      name="termsAndConditions"
                      label=""
                      placeholder="Enter Terms and Conditions"
                      required
                      rows={10}
                    />
                  </div>
                </form>
              </FormProvider>
            ) : termsAndConditions ? (
              <pre className="m-3">{termsAndConditions || ""}</pre>
            ) : (
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "200px",
                }}
              >
                <Empty description="No Terms and Conditions Provided for this Invoice" />
              </div>
            )}

            {isTermsAndConditionsChanged && (
              <ul className="actions clearfix d-flex">
                <li>
                  <Button
                    color="primary"
                    type="submit"
                    onClick={() => {
                      setTermsVisible(false);
                      setIsTermsAndConditionsChanged(true);
                      setShowSubmitBtn(true);
                    }}
                  >
                    Submit
                  </Button>
                </li>
                <li className="m-3 mt-0">
                  <Button
                    color="primary"
                    onClick={() => {
                      setIsTermsAndConditionsChanged(false);
                      setNewTermsAndConditions(termsAndConditions);
                    }}
                    className="btn-dim btn btn-secondary"
                  >
                    Cancel
                  </Button>
                </li>
              </ul>
            )}
          </AntModal>

          <AntModal
            title={undefined}
            visible={bankDetailsVisible}
            footer={null}
            onCancel={() => {
              setBankDetailsVisible(false);
            }}
            width="60%"
            bodyStyle={{ maxHeight: "70vh", overflowY: "auto" }}
          >
            <ShowSupplierBankInfo />
          </AntModal>

          {drawerVisible && (
            <Drawer
              title={
                <div className="d-flex justify-content-between align-items-center">
                  <strong>Create Dispatch</strong>
                  <div style={{ textAlign: "right" }}>
                    {disableSubmit ? (
                      <Button color="light" type="button">
                        <Spinner size="sm" color="light" />
                      </Button>
                    ) : (
                      <Button
                        onClick={() => {
                          form2.submit();
                        }}
                        color="primary"
                        className="m-3 mt-0 mb-0"
                      >
                        Create Dispatch
                      </Button>
                    )}
                  </div>
                </div>
              }
              placement="right"
              onClose={closeDrawer}
              open={drawerVisible}
              width="85vw"
              styles={{
                body: {
                  paddingTop: "0px",
                },
              }}
              className="p-0"
              style={{
                position: "absolute",
                zIndex: 1100,
              }}
            >
              <Form
                form={form2}
                layout="vertical"
                onFinish={(data) => {
                  setDisableSubmit(true);
                  Swal.fire({
                    title: "Are you sure?",
                    text: "You are about to create an dispatch !",
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonColor: "#3085d6",
                    cancelButtonColor: "#d33",
                    confirmButtonText: "Yes, Create !",
                  }).then((result) => {
                    if (result.isConfirmed) {
                      createDispatch(data);
                    } else {
                      setDisableSubmit(false);
                    }
                  });
                }}
                // requiredMark={false}
              >
                <AntCollapse
                  defaultActiveKey={["1", "2", "3"]}
                  className="mt-3"
                >
                  <Panel header={<h6>Driver Information</h6>} key="1">
                    <div className="d-flex justify-content-around ">
                      <div className="justify-start w-100  flex-wrap pt-2">
                        {!sequenceAccess?.includes("Dispatch") ? (
                          <Form.Item
                            name="DispatchId"
                            label={
                              <div className="data-label w-100 mb-0">
                                Dispatch Number
                              </div>
                            }
                            className="w-30 p-2 paddingLeft10  pt-0 pb-0"
                            rules={[
                              {
                                required: true,
                                message: "Enter Dispatch Number",
                              },
                            ]}
                          >
                            <Input
                              placeholder="Dispatch Number"
                              className="w-100"
                            />
                          </Form.Item>
                        ) : (
                          <></>
                        )}
                        <Form.Item
                          name="dispatchDate"
                          label={
                            <div className="data-label w-100 mb-0">
                              Dispatch Date
                            </div>
                          }
                          className="w-30 p-3 paddingLeft10 pt-0 pb-0"
                          rules={[
                            {
                              required: true,
                              message: "Please select Dispatch Date",
                            },
                          ]}
                        >
                          <DatePicker
                            className="w-100"
                            placeholder="Dispatch Date"
                            minDate={dayjs(materialData?.invoiceDate)}
                            maxDate={dayjs()}
                          />
                        </Form.Item>

                        <Form.Item
                          name="driverName"
                          label={
                            <div className="data-label w-100 mb-0">
                              Driver Name
                            </div>
                          }
                          className="w-30 p-3 paddingLeft10 pt-0 pb-0"
                          rules={[
                            {
                              required: true,
                              message: "Please enter Driver Name",
                            },
                            {
                              pattern: /^[a-zA-Z\s]*$/,
                              message:
                                "Driver Name should only contain letters and spaces",
                            },
                          ]}
                        >
                          <Input placeholder="Driver Name" />
                        </Form.Item>

                        <Form.Item
                          name="driverContact"
                          label={
                            <div className="data-label w-100 mb-0">
                              Driver Contact
                            </div>
                          }
                          className="w-30 p-3 paddingLeft10 pt-0 pb-0"
                          rules={[
                            {
                              required: true,
                              message: "Please enter Driver Contact",
                            },
                            {
                              pattern: /^[0-9]{10}$/,
                              message:
                                "Driver Contact should be a 10-digit number",
                            },
                          ]}
                        >
                          <InputNumber
                            className="w-100"
                            placeholder="Driver Contact"
                            formatter={(value) =>
                              `${value}`.replace(/\B(?=(\d{2})+(?!\d))/g, "")
                            }
                            onKeyPress={(event) => {
                              if (!/^[0-9]+$/.test(event.key)) {
                                event.preventDefault();
                              }
                            }}
                          />
                        </Form.Item>

                        <Form.Item
                          name="driverEmail"
                          label={
                            <div className="data-label w-100 mb-0">
                              Driver Email
                            </div>
                          }
                          className="w-30 p-3 paddingLeft10 pt-0 pb-0"
                          // rules={[
                          //   {
                          //     required: true,
                          //     message: "Please enter Driver Email",
                          //   },
                          //   {
                          //     type: "email",
                          //     message: "Please enter a valid email address",
                          //   },
                          // ]}
                        >
                          <Input placeholder="Driver Email" />
                        </Form.Item>

                        <Form.Item
                          name="vehicleNumber"
                          label={
                            <div className="data-label w-100 mb-0">
                              Vehicle Number {"("}
                              <span className="small">
                                {" "}
                                eg: MH12AB1234
                              </span>{" "}
                              {")"}
                            </div>
                          }
                          className="w-30 p-3 paddingLeft10 pt-0 pb-0"
                          rules={[
                            {
                              required: true,
                              message: "Please enter Vehicle Number",
                            },
                            {
                              pattern: /^[A-Z]{2}\d{2}[A-Z]{1,2}\d{4}$/,
                              message:
                                "Vehicle Number format should be 'XX00XX0000'",
                            },
                          ]}
                        >
                          <Input placeholder="Vehicle Number" />
                        </Form.Item>

                        <Form.Item
                          name="vehicleType"
                          label={
                            <div className="data-label w-100 mb-0">
                              {" "}
                              Vehicle Type
                            </div>
                          }
                          className="w-30 p-3 paddingLeft10 pt-0 pb-0"
                          // rules={[
                          //   {
                          //     required: true,
                          //     message: "Please select Vehicle Type",
                          //   },
                          // ]}
                        >
                          <Select placeholder="Select Invoice Type">
                            <Option value="tipper truck">Tipper truck</Option>
                            <Option value="concrete truck">
                              Concrete truck
                            </Option>
                            <Option value="pickup truck">Pickup truck</Option>
                            <Option value="small truck">Small truck</Option>
                            <Option value="box truck">Box truck</Option>
                            <Option value="boat haulage">Boat haulage</Option>
                            <Option value="flatbed truck">Flatbed truck</Option>
                            <Option value="tow truck">Tow truck</Option>
                            <Option value="chiller truck or reefers">
                              Chiller truck or reefers
                            </Option>
                            <Option value="crane trucks or mobile cranes">
                              Crane trucks or mobile cranes
                            </Option>
                            <Option value="logging truck">Logging truck</Option>
                            <Option value="tanker truck">Tanker truck</Option>
                            <Option value="furniture truck">
                              Furniture truck
                            </Option>
                            <Option value="snow plows">Snow plows</Option>
                            <Option value="trailer Truck">Trailer Truck</Option>
                            <Option value="off-road Truck">
                              Off-road Truck
                            </Option>
                          </Select>
                        </Form.Item>
                      </div>
                    </div>
                  </Panel>

                  <Panel header={<h6>Material Information</h6>} key="2">
                    <div className="table-responsive poTable">
                      <div
                        style={{
                          width: screenSize - 320 + "px",
                          margin: "0px auto 15px auto",
                        }}
                      >
                        <Table
                          className="customTable"
                          columns={mergedColumns.map((col: any) => {
                            if (col.dataIndex === "invoiceQuantity") {
                              return {
                                ...col,
                                hidden: true,
                                // render: (text: any, record: any) =>
                                //   `${Number(text)} ${record.uom}`,
                              };
                            } else if (col.dataIndex === "operation") {
                              return {
                                ...col,
                                hidden: true,
                              };
                            } else if (col.dataIndex === "availableQuantity") {
                              return {
                                ...col,
                                hidden: true,
                              };
                            } else if (col.dataIndex == "grossTotal") {
                              return {
                                ...col,
                                fixed: "right",
                              };
                            }
                            return {
                              ...col,
                            };
                          })}
                          dataSource={dispatchableLines}
                          scroll={{ x: 1000 }}
                          expandable={{
                            expandedRowRender: (record: any) => (
                              <div className="expandableBody">
                                <div style={{ flex: 1, textAlign: "left" }}>
                                  <p style={{ margin: 0 }}>
                                    {record.description}
                                  </p>
                                  {record.file && (
                                    <div>
                                      <PaperClipOutlined /> &nbsp;
                                      <a
                                        href={record?.file?.filePath}
                                        download={record?.file?.filePath}
                                      >
                                        {record?.file?.fileName}
                                      </a>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ),
                          }}
                          bordered
                          size="middle"
                          pagination={false}
                        />
                      </div>
                    </div>

                    {/* <div className="d-flex justify-content-end">
                    <ViewTableTotals
                      data={dispatchableLines}
                      additionalCharges={additionalCharges}
                      invoice={true}
                    />
                  </div> */}
                    <Row className="m-4 justify-content-end ">
                      {dispatchSignature?.isActive ? (
                        <div className="signatureBtnWrapper">
                          <div className="d-flex justify-content-between align-items-center">
                            <strong>Signature</strong>
                            <a
                              style={{
                                fontSize: "14px",
                                cursor: "pointer",
                              }}
                              onClick={() => {
                                setDispatchSignature((prev: any) => ({
                                  ...prev,
                                  isActive: false,
                                  image: null,
                                  label: "Authorised Signatory",
                                }));
                              }}
                            >
                              <Icon
                                name="cross"
                                className="pointer"
                                onClick={() => {
                                  setDispatchSignature((prev: any) => ({
                                    ...prev,
                                    isActive: false,
                                    image: null,
                                    label: "Authorised Signatory",
                                  }));
                                }}
                              />
                            </a>
                          </div>
                          <div
                            style={{
                              minHeight: dispatchSignature?.image
                                ? "0px"
                                : "120px",
                              display: dispatchSignature?.image
                                ? "block"
                                : "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {!dispatchSignature?.image && (
                              <Button
                                color="btn-dim btn"
                                onClick={() => {
                                  setShowCropModel(true);
                                }}
                              >
                                <Icon
                                  name="upload-cloud"
                                  className="pointer"
                                  onClick={() => {
                                    setShowCropModel(true);
                                  }}
                                />
                                <span>Upload</span>
                              </Button>
                            )}
                            {dispatchSignature?.image && (
                              <>
                                <div className="signatueImgWrapper border">
                                  <img
                                    src={dispatchSignature?.image}
                                    alt="Signed"
                                    style={{ width: "160px" }}
                                  />
                                </div>
                                <div
                                  style={{
                                    display: "flex",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  <Button
                                    color="btn-dim btn"
                                    onClick={() => {
                                      setDispatchSignature((pre: any) => {
                                        return { ...pre, image: null };
                                      });
                                    }}
                                  >
                                    <span>Reset</span>
                                    <Icon
                                      name="redo"
                                      className="pointer"
                                      onClick={() => {
                                        setDispatchSignature((pre: any) => {
                                          return { ...pre, image: null };
                                        });
                                      }}
                                    />
                                  </Button>
                                </div>
                                <div>
                                  <div className="form-group">
                                    <label
                                      className="form-label"
                                      htmlFor="signatureLabel"
                                    >
                                      Signature Label
                                    </label>
                                    <input
                                      type="text"
                                      className="form-control"
                                      name="signatureLabel"
                                      onChange={(e) => {
                                        setDispatchSignature((pre: any) => {
                                          return {
                                            ...pre,
                                            label: e.target.value,
                                          };
                                        });
                                      }}
                                      defaultValue={
                                        dispatchSignature?.label ||
                                        "Authorised Signatory"
                                      }
                                    />
                                  </div>
                                </div>
                              </>
                            )}
                          </div>

                          {!dispatchSignature?.image && (
                            <>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  flexDirection: "column",
                                }}
                              >
                                <Button
                                  color="btn-dim btn"
                                  onClick={() => setShowSignatureModel(true)}
                                >
                                  <Icon name="pen" />
                                  <span>Use Signature Pad</span>
                                </Button>
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        <div
                          style={{
                            border: "1px solid #e5ecf7",
                            backgroundColor: "#f7faff",
                            padding: "10px",
                            marginLeft: "10px",
                            width: "22%",
                            borderRadius: "5px",
                            textAlign: "center",
                          }}
                        >
                          <Button
                            color="btn-dim btn"
                            onClick={() => {
                              setDispatchSignature((pre: any) => {
                                return { ...pre, isActive: true };
                              });
                            }}
                          >
                            <Icon
                              name="edit"
                              className="pointer"
                              onClick={() => {
                                setDispatchSignature((pre: any) => {
                                  return { ...pre, isActive: true };
                                });
                              }}
                            />
                            <span>Add Signature</span>
                          </Button>
                        </div>
                      )}
                    </Row>
                  </Panel>
                  <Panel header={<h6>Attachments (If Any) </h6>} key="3">
                    <Form.Item
                      label={undefined}
                      valuePropName="fileList"
                      getValueFromEvent={(e: any) => e?.fileList}
                      rules={[
                        {
                          required: true,
                          message: "Please upload at least one document.",
                        },
                      ]}
                    >
                      <Dragger
                        beforeUpload={beforeUpload}
                        onChange={handleChange}
                        fileList={attachments}
                        maxCount={3}
                        multiple={true}
                        showUploadList={{ showPreviewIcon: false }}
                        itemRender={(originNode, file, fileList, actions) => {
                          // Customize the rendering of each file to remove the tooltip
                          return React.cloneElement(originNode, {
                            title: null, // Removes the tooltip entirely
                          });
                        }}
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.html,.hbs"
                      >
                        <AntButton icon={<UploadOutlined />}>
                          Click to Upload
                        </AntButton>
                      </Dragger>
                    </Form.Item>
                  </Panel>
                </AntCollapse>
                {showCropModel && (
                  <CropperComponent
                    modal={showCropModel}
                    toggle={() => setShowCropModel(!showCropModel)}
                    onSave={(data: string) => {
                      setDispatchSignature((pre: any) => {
                        return { ...pre, image: data };
                      });
                      setShowCropModel(false);
                    }}
                  />
                )}
                {showSignatureModel && (
                  <SignatureModal
                    modal={showSignatureModel}
                    toggle={() => setShowSignatureModel(!showSignatureModel)}
                    onSave={(data: string) => {
                      setDispatchSignature((pre: any) => {
                        return { ...pre, image: data };
                      });
                      setShowSignatureModel(false);
                    }}
                  />
                )}
              </Form>
            </Drawer>
          )}
        </div>
      )}
    </div>
  );
}

const RejectInvoice = ({
  isModalVisible,
  setIsModalVisible,
  rejectInvoice,
}: any) => {
  const [form] = Form.useForm();

  const handleCancel = () => {
    setIsModalVisible(false);
  };

  const handleOk = () => {
    form
      .validateFields()
      .then((values) => {
        const reason = values.reason;
        rejectInvoice(reason); // Call your reject function here
        form.resetFields();
        setIsModalVisible(false);
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  return (
    <AntModal
      title="Reject Purchase Order"
      visible={isModalVisible}
      footer={null} // Remove the default footer buttons
      onCancel={handleCancel}
    >
      <Form form={form} layout="vertical" name="rejectForm">
        <Form.Item
          name="reason"
          label="Reason for rejection"
          rules={[{ required: true, message: "Please input your reason!" }]}
        >
          <Input.TextArea placeholder="Type your reason here..." />
        </Form.Item>
        <div className="actions clearfix">
          <ul>
            <li>
              <Button color="primary" type="submit" onClick={handleOk}>
                Submit
              </Button>
            </li>
            <li>
              <Button
                color="primary"
                onClick={handleCancel}
                className="btn-dim btn btn-secondary"
              >
                Cancel
              </Button>
            </li>
          </ul>
        </div>
      </Form>
    </AntModal>
  );
};

export default InvoiceComponent;
